import React, { Fragment } from 'react';
import classNames from 'classnames';
import { pathOr } from 'ramda';

import { DATE_TYPE_DATE } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import {
  getQuantity,
  getUserTxRole,
  txIsEnquired,
  txRoleIsCustomer,
} from '../../util/transactions';
import { formatMoney } from '../../util/currency';
import { createSlug } from '../../util/urlHelpers';
import { BookingTimeInfo, NamedLink, ResponsiveImage } from '../../components';

import css from './InboxPage.module.css';

// Functional component as a helper to build BookingDetails section
const DetailsSection = props => {
  const {
    className,
    rootClassName,
    currentTransaction,
    currentUser,
    stateData,
    type,
    unitType,
    intl,
  } = props;
  const isOrder = type === 'order';
  const isEnquiry = txIsEnquired(currentTransaction);
  const currentListing = currentTransaction.listing;
  const currentBooking = currentTransaction.booking;
  const listingId = pathOr('', ['id', 'uuid'], currentListing);
  const listingTitle = pathOr('', ['attributes', 'title'], currentListing);
  const slug = createSlug(listingTitle);
  const image = pathOr(null, ['images'], currentListing) && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const transactionRole = getUserTxRole(currentUser.id, currentTransaction);
  const isCustomer = txRoleIsCustomer(transactionRole);

  const txProtectedData = pathOr({}, ['attributes', 'protectedData'], currentTransaction);
  const enquiryDate = txProtectedData.date;
  const canShowSearch = stateData.showSearch && txProtectedData.fromSearch;
  const searchMoreMessage = intl.formatMessage({ id: 'InboxPage.searchMore' });
  const quantity = isEnquiry ? txProtectedData.quantity : getQuantity(currentTransaction);
  const participantsTitle = <FormattedMessage id={'InboxPage.participants'} />;
  const quantityMessage = isEnquiry && (
    <FormattedMessage id={'InboxPage.quantity'} values={{ quantity }} />);
  const adultsMessage = quantity && (
    <FormattedMessage id={'InboxPage.adults'} values={{ quantity: quantity.adults }} />
  );
  const childrenMessage = quantity && quantity.children > 0 && (
    <FormattedMessage id={'InboxPage.children'} values={{ quantity: quantity.children }} />
  );
  const infantsMessage = quantity && quantity.infants > 0 && (
    <FormattedMessage id={'InboxPage.infants'} values={{ quantity: quantity.infants }} />
  );

  const bookingPrice = isOrder ? currentTransaction.attributes.payinTotal : currentTransaction.attributes.payoutTotal;
  const price = bookingPrice && formatMoney(intl, bookingPrice);
  const priceMessage = <FormattedMessage id={isOrder ? 'InboxPage.price' : 'InboxPage.income'} />;

  const moreDetailsMessage = intl.formatMessage({ id: 'InboxPage.moreDetails' });
  const bookingMessage = intl.formatMessage({ id: 'InboxPage.bookingRequest' });

  const classes = classNames(rootClassName || css.detailsContainer, className);
  const stateClasses = classNames(css.stateDetails, stateData.stateClassName);

  return (
    <div className={classes}>
      <div className={css.topDetailsContainer}>
        <FormattedMessage id='InboxPage.details' />
      </div>
      <div className={css.detailsContainer}>
        <div className={css.detailsContent}>
          <div className={css.detailsSection}>
            <span className={stateClasses}>{stateData.state}</span>
            {isEnquiry && isCustomer ? (
              <div className={css.moreDetailsContainer}>
                <NamedLink
                  className={css.bookingLink}
                  name='ListingPage'
                  params={{ id: listingId, slug }}
                >
                  {bookingMessage}
                </NamedLink>
              </div>
            ) : null}
            {canShowSearch ? (
              <NamedLink
                className={css.searchMoreLink}
                name='SearchPage'
                to={{ search: txProtectedData.fromSearch }}
              >
                <span>{searchMoreMessage}</span>
              </NamedLink>
            ) : null}
          </div>
          <div className={css.detailsSection}>
            <NamedLink
              className={css.listingLink}
              name='ListingPage'
              params={{ id: listingId, slug }}
            >
              {isCustomer && (
                <ResponsiveImage
                  className={css.listingImage}
                  alt={listingTitle}
                  image={image}
                  variants={['landscape-crop']}
                />
              )}
              <span>{listingTitle}</span>
            </NamedLink>
          </div>
          {currentBooking || enquiryDate ? (
            <div className={css.detailsSection}>
              <BookingTimeInfo tx={currentTransaction} intl={intl} />
            </div>
          ) : null}
          {quantity ? (
            <div className={css.detailsSection}>
              {participantsTitle}
              <div className={css.participants}>
                {!quantity.adults ? (
                  quantityMessage
                ) : (
                  <Fragment>
                    {adultsMessage}
                    {childrenMessage}
                    {infantsMessage}
                  </Fragment>
                )}
              </div>
            </div>
          ) : null}
          {!isEnquiry ? (
            <div className={css.detailsSection}>
              <div className={css.priceContainer}>
                {priceMessage}
                <span className={css.price}>{price}</span>
              </div>
            </div>
          ) : null}
          {!isEnquiry ? (
            <div className={css.moreDetailsContainer}>
              <NamedLink
                className={css.moreDetailsLink}
                name={isOrder ? 'OrderDetailsPage' : 'SaleDetailsPage'}
                params={{ id: currentTransaction.id.uuid }}
              >
                {moreDetailsMessage}
              </NamedLink>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DetailsSection;



// WEBPACK FOOTER //
// ./src/containers/InboxPage/DetailsSection.js